import { useEffect, useState, useRef, useMemo} from 'react'
import { ChatsView } from '../../views'
import socketio from 'socket.io-client'
import { useGlobalState } from '../../../GlobalState'
import { LocalStorageService, ChatbotService, UsageService } from '../../../services'
import { useLocation, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Container, Grid } from '@material-ui/core'
import { TabPure, ModalUtil } from '../../pures'
import { SimpleLoading } from '../../pures'
import { useConversationVisibility } from '../../../hooks'

const ChatsAction = () => {
  // #region variables
  const url = process.env.REACT_APP_ETICKET_API
  const chatbotUrl = process.env.REACT_APP_CHATBOT_URL
  const location = useLocation()
  const history = useHistory()
  const convQueryParam = new URLSearchParams(location.search).get('conversationId')
  const usageService = new UsageService(url)
  const chatbotService = new ChatbotService(url)
  const localStorageService = new LocalStorageService()
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const userId = localStorageService.getLocalStorageItem('user', true)?.userMetaData?.userA3Cloud?.id
  const socketRef = useRef(null)
  const chatsRef = useRef([])
  const selectedIdRef = useRef(selectedId)
  const { botConfig, setBotConfig, chatbotList, lastChatNotification, chats, setChats, myChats, setMyChats, assignedChats, setAssignedChats, disableNotificationByConversationId } = useGlobalState()
  // Refs to avoid stale closures in async callbacks
  const botConfigRef = useRef(botConfig)
  const chatbotListRef = useRef(chatbotList)
  // Holds a botConfigId to switch to once chatbotList is populated (email-link timing fix)
  const pendingBotConfigIdRef = useRef(null)
  const [conversationProp, setConversationProp] = useState({
    id: convQueryParam,
    counter: 0
  })
  const [socketEvent, setSocketEvent] = useState({event: false, conversationId: null})
  const [tabSelected, setTabSelected] = useState(convQueryParam ? 'mychats' : 'general')
  const { t, i18n, ready } = useTranslation('chats')
  const { t: tCommon } = useTranslation()
  const tabs = useMemo(() => {
    if (!ready) return []

    return  [
    {
      route: 'general',
      title: t('tab.general'),
      key: 'tab.general',
      selected: (tabSelected === 'general') ? true : false
    }, {
      route: 'mychats',
      title: t('tab.myChats'),
      key: 'tab.myChats',
      selected: (tabSelected === 'mychats') ? true : false
    }
  ]}, [tabSelected, t, ready])

  const [categoriesUsers, setCategoriresUsers] = useState([])
  const modalUtil = new ModalUtil()
  // #endregion variables
  
  // #region effects
  useEffect(() => {
    try {
      if (conversationProp.id) {
          handleGetBotConfigByConversationId(conversationProp.id)
          removeConversationIdFromUrl()
      }
      handleGetMyChats(conversationProp.id || null, userId)

      // categries and users to re-assign conversation
      handleGetCategoriesUsers()

      // Handle tab-switch for notification that arrived before this component mounted
      if (lastChatNotification) {
        const { conversationId, user } = lastChatNotification.data || {}
        if (conversationId && user === userId && !selectedId) {
          handleUpdateTabStatus('mychats')
          setSocketEvent({event: true, conversationId})
        }
      }
    } catch (e) {
      console.error(`An error occurred in useEffect when chats changed: ${e}`)
    }
  }, [])

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search)
      const conversationId = searchParams.get('conversationId')
      if (conversationId && conversationId !== conversationProp.id) {
        setConversationProp({id: conversationId, counter: conversationProp.counter + 1})
        handleGetBotConfigByConversationId(conversationId)
      }
    } catch (e) {
      console.error(`An error occurred in effect to get changes in route: ${e.message}`)
    }
  }, [location.search])

  useEffect(() => {
    chatsRef.current = chats
  }, [chats])

  useEffect(() => {
    selectedIdRef.current = selectedId
  }, [selectedId])

  useEffect(() => {
    botConfigRef.current = botConfig
  }, [botConfig])

  useEffect(() => {
    chatbotListRef.current = chatbotList
    // If a bot switch was deferred because chatbotList was empty, retry now
    if (pendingBotConfigIdRef.current && chatbotList.length > 0) {
      const targetBot = chatbotList.find(c => c.id === pendingBotConfigIdRef.current)
      if (targetBot && botConfigRef.current?.id !== targetBot.id) {
        pendingBotConfigIdRef.current = null
        setBotConfig(targetBot)
        setConversationProp(prev => ({...prev, counter: prev.counter + 1}))
      }
    }
  }, [chatbotList])

  useEffect(() => {
    // Cleanup previous socket connection if exists
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
    }

    // Only proceed if we have a valid botConfig
    if (!botConfig?.id) {
      setLoading(true)
      return
    }

    // Load chats
    handleGetChats()

    // Connect socket
    socketRef.current = socketio.connect(process.env.REACT_APP_SOCKET_ASSISTANT)

    socketRef.current.on('connect', () => {
    })

    socketRef.current.on('connect_error', (error) => {
    })

    socketRef.current.on('newConversationCreated', (response) => {
      const timezone = localStorageService.getLocalStorageItem('timezone')
      const payload = (response.updatedDate && timezone)
        ? {
            ...response,
            updatedDate: new Intl.DateTimeFormat('sv-SE', {
              timeZone: timezone,
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', second: '2-digit',
              hour12: false
            }).format(new Date(response.updatedDate))
          }
        : response
      handleAddConversation(payload)
    })

    socketRef.current.on('conversationClosed', (response) => {
      handleMarkConversationClosed(response)
    })

    socketRef.current.on('messageActivity', (response) => {
      handleReloadChats(response)
    })

    socketRef.current.on('changeMode', (response) => {
      handleCheckConversationModeChanged(response)
    })

    socketRef.current.on('newMessageUnseen', (data) => {
      try {
        const { conversationId, unseenMessages } = data
        // Do not increment if this is the currently selected conversation.
        if (conversationId && unseenMessages && selectedIdRef.current !== conversationId) {
          const updatedChats = chatsRef.current.map(chat => {
            if (chat.conversationId === conversationId) {
              return {
                ...chat,
                unseenMessages: (chat.unseenMessages || 0) + unseenMessages
              }
            }
            return chat
          })
          setChats(updatedChats)
        }
      } catch (e) {
        console.error(`An error occurred in newMessageUnseen handler: ${e}`)
      }
    })

    socketRef.current.on('messageSeen', (data) => {
      try {
        const { conversationId, messageIds, seenBy, timestamp } = data
        
        // Only process the event if it is relevant to the dashboard.
        if (seenBy !== 'end_user') return
        
        // Validate that the event belongs to a conversation in the list.
        if (!chatsRef.current.some(c => c.conversationId === conversationId)) return
        
        // Update the chat list (for badges, etc.).
        const updatedChats = chatsRef.current.map(chat => {
          if (chat.conversationId === conversationId) {
            return { ...chat, lastSeenByEndUser: timestamp }
          }
          return chat
        })
        
        setChats(updatedChats)
        
        // If this is the active conversation, notify the iFrame to refresh its messages.
        if (selectedIdRef.current === conversationId) {
          setTimeout(() => {
            const iframe = document.getElementById('iframeChatbot')
            
            if (iframe?.contentWindow) {
              const chatbotOrigin = process.env.REACT_APP_CHATBOT_URL
              
              iframe.contentWindow.postMessage({
                type: 'messagesSeenUpdate',
                conversationId,
                messageIds,
                timestamp
              }, chatbotOrigin)
            }
          }, 50)
        }
        
      } catch (e) {
        console.error(`Error handling messageSeen in dashboard: ${e}`)
      }
    })

    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.off('messageSeen')
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [botConfig?.id])

  // Handle socket rooms when the selected conversation changes.
  useEffect(() => {
    // Only proceed if the socket is connected and a conversation is selected.
    if (!socketRef.current?.connected || !selectedId) return
    
    socketRef.current.emit('join', selectedId)
    
    // Cleanup: leave the room when the component unmounts or selectedId changes.
    return () => { if (selectedId && socketRef.current?.connected) socketRef.current.emit('leave', selectedId) }
  }, [selectedId])

  // Auto-select conversation from URL param once chats/myChats are loaded.
  // Covers both "general" (unattended) and "mychats" (assigned) tabs.
  useEffect(() => {
    try {
      if (!conversationProp.id) return
      const isInMyChats = myChats.includes(conversationProp.id)
      const isInGeneral = chats.some(c => c.conversationId === conversationProp.id)
      if (isInMyChats) {
        handleUpdateTabStatus('mychats')
        setSelectedId(conversationProp.id)
        setConversationProp(prev => ({id: null, counter: prev.counter}))
      } else if (isInGeneral) {
        handleUpdateTabStatus('general')
        setSelectedId(conversationProp.id)
        setConversationProp(prev => ({id: null, counter: prev.counter}))
      }
    } catch (e) {
      console.error(`An error occurred in auto-select from URL param: ${e.message}`)
    }
  }, [chats, myChats])

  useEffect(() => {
    if (tabSelected && socketEvent.event) {
      setSelectedId(socketEvent.conversationId)
      setSocketEvent({event: false, conversationId: null})
      handleUpdateTabStatus('mychats')
    }
  }, [tabSelected, socketEvent.event])

  /**
   * @author
   * @updatedBy Jorge Perales
   * @description React to global lastChatNotification state set by app.action.js socket listener.
   * Handles chat list and myChats reload regardless of whether the user is currently on /chats.
   * @createdDate
   * @updatedDate 25/03/2025
   * @listens lastChatNotification GlobalState
   */
  useEffect(() => {
    try {
      if (!lastChatNotification) return
      const { conversationId, user } = lastChatNotification.data || {}
      if (conversationId && user === userId && !selectedId) {
        handleUpdateTabStatus('mychats')
        setSocketEvent({event: true, conversationId})
      }
    } catch (e) {
      console.error(`An error occurred in useEffect for lastChatNotification: ${e}`)
    }
  }, [lastChatNotification])
  
  
  // ➕ Tracking de visibilidad para sistema de "visto"
  useConversationVisibility({
    conversationId: selectedId,
    socketRef,
    viewedBy: 'support_agent',
    enabled: !!botConfig?.id && socketRef.current?.connected
  })
  // #endregion effects

  const handleGetChats = async () => {
    if (botConfig?.id) {
      const token = localStorageService.getLocalStorageItem('token', true)?.accessToken
      const response = await usageService.getConversationsByMonth(token, botConfig.id)
      setChats(response)
      setLoading(false)
      setToken(token)
    }
  }

  /**
   * @description method to reload chats
   * @param clientId clientId to validate if we need to do reload
   */
  const handleReloadChats = async (clientId) => {
    try {
      const localClientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData.userA3Cloud.clientId
      if (localClientId === clientId) {
        const currentToken = localStorageService.getLocalStorageItem('token', true)?.accessToken
        const response = await usageService.getConversationsByMonth(currentToken, botConfigRef.current?.id)
        setChats(response)
      }
    } catch (error) {
      console.error(`An error occurred in handleReloadChats: ${error}`)
    }
  }

  /**
   * @author Jorge Perales
   * @description Prepend a new conversation to the chats list using the full payload from the socket event
   * @date 18-05-2026
   * @param {Object} payload { clientId, botConfigId, categorySupportId, conversationId, finished, lastMessageDate, mode, unseenMessages, updatedDate, userData, createdDate }
   */
    const handleAddConversation = ({
      clientId,
      botConfigId,
      categorySupportId,
      conversationId,
      finished,
      lastMessageDate,
      mode,
      unseenMessages,
      updatedDate,
      sourceChannel,
      userData
    }) => {
      try {
        const localClientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData.userA3Cloud.clientId
        if (localClientId !== clientId) return

        const alreadyExists = chatsRef.current.some(c => c.conversationId === conversationId)
        if (alreadyExists) return

        const conversation = {
          botConfigId,
          categorySupportId,
          conversationId,
          finished,
          lastMessageDate,
          mode,
          unseenMessages,
          updatedDate,
          sourceChannel: sourceChannel || 'web',
          userData: userData || null
        }

        setChats(prev => [conversation, ...prev])
      } catch (error) {
        console.error(`An error occurred in handleAddConversation: ${error}`)
      }
    }

  /**
   * @author Jorge Perales
   * @description Mark a conversation as finished in local state without an HTTP call
   * @date 18-05-2026
   * @param {Object} payload { clientId, conversationId }
   */
  const handleMarkConversationClosed = (payload = {}) => {
    if (!payload || !payload?.conversationId) {
      console.error('Invalid payload for handleMarkConversationClosed:', payload)
      return
    }

    const { clientId, conversationId } = payload
    try {
      const localClientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData.userA3Cloud.clientId
      if (localClientId !== clientId) return
      setChats(prev => prev.map(c => c.conversationId === conversationId ? { ...c, finished: true } : c))
    } catch (error) {
      console.error(`An error occurred in handleMarkConversationClosed: ${error}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description event to detect if conversation mode changed
   * @param conversationId | string
   */
  const handleCheckConversationModeChanged = async ({conversationId}) => {
    try {
      if (chatsRef.current && chatsRef.current.length > 0 && chatsRef.current.some(c => c.conversationId === conversationId)) {
        const localClientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData.userA3Cloud.clientId
        handleReloadChats(localClientId)
      }
    } catch (error) {
      console.error(`An error occurred in handleReloadChats: ${error}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description method to update seen support messages
   * @date 06-11-2025
   * @param {string} conversationId | conversation id
   * @return void
   */
  const handleUpdateSeenSupportMessages = async (conversationId) => {
    try {
      await usageService.messagesSeen(conversationId, 'user')
      setChats(prevChats => prevChats.map(c => c.conversationId === conversationId ? {...c, unseenMessages: 0} : c))
      socketRef.current.emit('messagesUserSeen', { conversationId })
      if (tabSelected === 'mychats') {
        disableNotificationByConversationId(conversationId)
      }
    } catch (e) {
      console.error(`An error occurred in handleUpdateSeenSupportMessages: ${e}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description method to get botconfig by conversation id
   * @createdDate 31-12-25
   * @param {*} conversationId conversation id
   * @return {void} void
   */
  const handleGetBotConfigByConversationId = async (conversationId) => {
    try {
      const token = localStorageService.getLocalStorageItem('token', true)?.accessToken
      const response = await chatbotService.getChatbotByConversationId(conversationId, token)
      handleUpdateSeenSupportMessages(conversationId)
      // Use refs to get the latest botConfig/chatbotList values and avoid stale closure.
      // Also handle botConfig being null (first load via email link).
      const currentBotConfig = botConfigRef.current
      const currentChatbotList = chatbotListRef.current
      if (response.botConfigId && currentBotConfig?.id !== response.botConfigId) {
        const targetBot = currentChatbotList.find(c => c.id === response.botConfigId)
        if (targetBot) {
          pendingBotConfigIdRef.current = null
          setBotConfig(targetBot)
          setConversationProp(prev => ({...prev, counter: prev.counter + 1}))
        } else {
          // chatbotList not ready yet — defer the switch until it populates
          pendingBotConfigIdRef.current = response.botConfigId
        }
      }
    } catch (e) {
      console.error(`An error occurred in handleGetBotConfigByConversationId: ${e}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description update tab selected
   * @createdDate 07-01-26
   * @param {*} value tab selected value
   * @return {void}
   */
  const handleUpdateTabStatus = (value) => {
    try {
      setTabSelected(value)
    } catch(e) {
      console.error(`An error occurred in handleUpdateTabStatus: ${e.message}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description event to get chats by userId
   * @createdDate 07-01-26
   * @return {void}
   */
  const handleGetMyChats = async (conversationId = null, user = null) => {
    try {
      const token = localStorageService.getLocalStorageItem('token', true)?.accessToken
      const response = await usageService.getChatsByUserId(userId, token)
      const { userChats, assignedChats } = response || {}
      setMyChats(userChats || [])
      setAssignedChats(assignedChats || [])
      if (response && conversationId && user === userId) {
        handleUpdateTabStatus('mychats')
        setSocketEvent({event: true, conversationId})
      }
    } catch (e) {
      console.error(`An error occurred in handleGetMyChats: ${e.message}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description Removes the 'conversationId' query param from the URL
   * @createdDate 15-01-26
   * @return {void}
   */
  const removeConversationIdFromUrl = () => {
    try {
      const searchParams = new URLSearchParams(location.search)

      // Check if the parameter exists before attempting to remove it
      if (searchParams.has('conversationId')) {
        searchParams.delete('conversationId') // Remove the parameter

        // Update the URL without reloading the page
        const newSearch = searchParams.toString()
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

        history.replace(newUrl) // 'replace' doesn't create a new history entry
        // Or use history.push(newUrl) if you want to be able to go back
      }
    } catch (e) {
      console.error(`An error occurred in removeConversationIdFromUrl: ${e.message}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description get categories and users to re-assign conversation
   * @date 20-01-26
   * @return {void}
   * @memberof ChatsAction
   */
  const handleGetCategoriesUsers = async () => {
    try {
      const clientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData.userA3Cloud.clientId
      const token = localStorageService.getLocalStorageItem('token', true)?.accessToken
      const response = await usageService.getCategoriesUsers(clientId, token)
      if (response) setCategoriresUsers(response)
    } catch (e) {
      console.error(`An error occurred in handleGetCategoriesUsers: ${e.message}`)
    }
  }

  /**
   * @author Antonio Vazquez
   * @description assign conversation
   * @date 20-01-26
   * @param conversationId conversation id
   * @param categoryId category id
   * @param userId user id
   * @return {Object}
   * @memberof ChatsAction
   */
  const handleAssignChat = async (conversationId, categoryId, userId) => {
    try {
      setSelectedId(null)
      modalUtil.showModal('loading', tCommon('modal.pure.loading.message'), tCommon('modal.pure.loading.title'))
      const result = await usageService.putAssignConversation(conversationId, categoryId, userId)
      if (result) {
          modalUtil.showModal('success', tCommon('modal.chat.assigned'), tCommon('modal.pure.title.success'))
          handleGetChats()
          handleGetMyChats(null, null)
      } else {
        modalUtil.showModal('error', tCommon('modal.pure.message.error'), tCommon('modal.pure.title.error'))
      }
    } catch (e) {
      console.error(`An error occurred in handleAssignChat: ${e.message}`)
      modalUtil.showModal('error', tCommon('modal.pure.message.error'), tCommon('modal.pure.title.error'))
    }
  }
  // #endregion methods

  // #region jsx-elements
  if (!ready) {
    return (
      <Container className='view-container'>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <SimpleLoading />
            </div>
          </Grid>
        </Grid>
      </Container>
    )
  }
  // #endregion jsx-elements

  return (
    <Container className='view-container'>
      <Grid container spacing={3}>
        <TabPure key={tabSelected} tabs={tabs} tabStatus={handleUpdateTabStatus} language={i18n.language} transl={t} />
        <Grid item xs={12}>
          {
            tabSelected === 'general' ?
            <ChatsView
              key={'tab.general'}
              chatbotUrl={chatbotUrl}
              conversations={chats.filter(c => !(assignedChats?.some(mc => mc === c.conversationId))) || []}
              isLoading={loading}
              token={token}
              handleOnClickConversation={handleUpdateSeenSupportMessages}
              botId={botConfig?.id}
              conversationIdParam={conversationProp}
              userId={userId}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              assignButton={true}
              assignData={categoriesUsers}
              onAssignChat={handleAssignChat}
              /> :
              tabSelected === 'mychats' &&
              <ChatsView
              key={'tab.myChats'}
              chatbotUrl={chatbotUrl}
              conversations={myChats?.map(mc => chats.find(c => c.conversationId === mc)).filter(v => v) || []}
              isLoading={loading}
              token={token}
              handleOnClickConversation={handleUpdateSeenSupportMessages}
              botId={botConfig?.id}
              conversationIdParam={conversationProp}
              userId={userId}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              assignButton={true}
              assignData={categoriesUsers}
              onAssignChat={handleAssignChat}
              />
          }
        </Grid>
      </Grid>
    </Container>
  )
}

export {ChatsAction as default}
